import { useHistory, useRouteMatch } from 'react-router-dom';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { isNil } from 'lodash/fp';
import { WAREHOUSE_CARDS_ROWS_PER_PAGE } from '../constants';
import { getUrlParams, setUrlParams } from '../../../../../utils/helpers/urlParams';
import { SortOrder } from '../../../../../generated/graphql';

type ParamType = false |string | null | number | string | string[];

const useSearchParams = () => {
    const history = useHistory();
    const { isExact } = useRouteMatch();

    const [nameOrSku, setNameOrSku] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [storageUnit, setStorageUnit] = useState<string | null>(null);
    const [leftoversSort, setLeftoversSort] = useState<string | false>(false);
    const [nameSort, setNameSort] = useState<string | false>(SortOrder.Asc);
    const [rowsPerPage, setRowsPerPage] = useState(WAREHOUSE_CARDS_ROWS_PER_PAGE);
    const [page, setPage] = useState(0);

    const handleUrlParamSet = <T extends ParamType>(setState: Dispatch<SetStateAction<T>>) =>
        (paramObj: Record<string, ParamType>) => {
            if(isExact){
                const paramName = Object.keys(paramObj)[0];
                const param = paramObj[paramName];
                setState(param as T);
                const newUrlParams = setUrlParams(history.location.search, {
                    [paramName]: isNil(param) ? '' : param || '',
                });

                history.push({ search: newUrlParams.toString() });
            }
        };

    useEffect(() => {
        if(isExact){
            const [
                nameOrSkuParam,
                selectedCategoryIdParam,
                storageUnitParam,
                nameSortParam,
                rowsPerPageParam,
                leftoversSortParam,
                pageParam,
            ] = getUrlParams({
                searchString: history.location.search,
                params: ['nameOrSku', 'selectedCategoryId', 'storageUnit', 'nameSort', 'rowsPerPage', 'leftoversSort', 'page']
            });

            setNameOrSku(nameOrSkuParam || '');
            setSelectedCategoryId(selectedCategoryIdParam || null);
            setStorageUnit(storageUnitParam || null);
            setNameSort(nameSortParam || false);
            setLeftoversSort(leftoversSortParam || false);
            setRowsPerPage(rowsPerPageParam ? parseInt(rowsPerPageParam, 10) : WAREHOUSE_CARDS_ROWS_PER_PAGE);
            setPage(pageParam ? parseInt(pageParam, 10) : 0);

            const newUrlParams = setUrlParams(history.location.search, {
                nameOrSku: nameOrSkuParam || '',
                selectedCategoryId: selectedCategoryIdParam || '',
                storageUnit: storageUnitParam || '',
                nameSort: nameSortParam || '',
                rowsPerPage: rowsPerPageParam || WAREHOUSE_CARDS_ROWS_PER_PAGE,
                leftoversSort: leftoversSortParam || '',
                page: pageParam || 0,
            });

            history.push({ search: newUrlParams.toString() });
        }
    // eslint-disable-next-line
    }, [isExact]);

    const handleSetNameOrSku = (q: ParamType) => handleUrlParamSet(setNameOrSku)({ nameOrSku: q });
    const handleSetSelectedCategoryId = (c: ParamType) => handleUrlParamSet(setSelectedCategoryId)({ selectedCategoryId: c });
    const handleSetStorageUnit = (s: ParamType) => handleUrlParamSet(setStorageUnit)({ storageUnit: s });
    const handleSetNameSort = (ns: ParamType) => handleUrlParamSet(setNameSort)({ nameSort: ns });
    const handleSetRowsPerPage = (p: ParamType) => handleUrlParamSet(setRowsPerPage)({ rowsPerPage: p });
    const handleSetLeftoversSort = (p: ParamType) => handleUrlParamSet(setLeftoversSort)({ leftoversSort: p });
    const handleSetPage = (r: ParamType) => handleUrlParamSet(setPage)({ page: r });

    return {
        nameOrSku, setNameOrSku: handleSetNameOrSku,
        selectedCategoryId, setSelectedCategoryId: handleSetSelectedCategoryId,
        storageUnit, setStorageUnit: handleSetStorageUnit,
        nameSort, setNameSort: handleSetNameSort,
        rowsPerPage, setRowsPerPage: handleSetRowsPerPage,
        leftoversSort, setLeftoversSort: handleSetLeftoversSort,
        page, setPage: handleSetPage,
    };
};

export default useSearchParams;
